import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Check, Eye, EyeOff, LoaderCircle } from "lucide-react";
import { loginRequest } from "../../slices/authActions";
import { useNotification } from "../../../../hooks/useNotification";

const FEATURES = [
  "Quản lý nhiều tài khoản Zalo trên cùng 1 giao diện",
  "Phân quyền nhân viên, gắn tag và ghi chú khách hàng",
  "Auto-assign hội thoại và Bot phản hồi sự kiện",
];

const LoginPage = () => {
  const dispatch = useDispatch();
  const { addNotification } = useNotification();
  const auth = useSelector((state) => state.auth);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    dispatch(loginRequest({ username, password }));
  };

  useEffect(() => {
    if (auth.isError) addNotification(auth.errorMessage, -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex">
      {/* Left panel: branding — desktop only */}
      <aside className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-primary/5 to-primary/15 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-blue-200/40 blur-3xl" />

        <div className="relative flex items-center gap-2">
          <img
            src="/icon-akabiz-zalo.png"
            alt="akaBiz"
            className="w-8 h-8 rounded"
          />
          <span className="text-[15px] font-semibold text-gray-900">
            akaBiz Chat
          </span>
        </div>

        <div className="relative space-y-5 max-w-md">
          <h1 className="text-[28px] leading-tight font-bold text-gray-900">
            Chăm sóc khách hàng
            <br />
            qua Zalo chuyên nghiệp
          </h1>
          <p className="text-[13px] text-gray-600 leading-relaxed">
            Nền tảng chat B2B giúp đội ngũ nhân viên phản hồi khách nhanh hơn,
            không bỏ sót hội thoại và tăng tỉ lệ chốt đơn.
          </p>
          <ul className="space-y-2 pt-2">
            {FEATURES.map((feat) => (
              <li
                key={feat}
                className="flex items-start gap-2 text-[12px] text-gray-700"
              >
                <span className="mt-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-primary/15 text-primary flex-shrink-0">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>{feat}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-[11px] text-gray-400">
          © {new Date().getFullYear()} akaBiz · chat.akabiz.net
        </p>
      </aside>

      {/* Right panel: form */}
      <main className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <img
              src="/icon-akabiz-zalo.png"
              alt="akaBiz"
              className="w-8 h-8 rounded"
            />
            <span className="text-[15px] font-semibold text-gray-900">
              akaBiz Chat
            </span>
          </div>

          <div className="space-y-1 mb-6">
            <h2 className="text-[20px] font-semibold text-gray-900">
              Đăng nhập
            </h2>
            <p className="text-[12px] text-gray-500">
              Chào mừng quay lại. Nhập tài khoản để tiếp tục.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="username-inp"
                className="block text-[12px] font-medium text-gray-700 mb-1.5"
              >
                Tên đăng nhập
              </label>
              <input
                type="text"
                id="username-inp"
                name="username-inp"
                autoComplete="username"
                autoFocus
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Nhập tên đăng nhập"
                className="w-full px-3 py-2 text-[13px] bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="password-inp"
                className="block text-[12px] font-medium text-gray-700 mb-1.5"
              >
                Mật khẩu
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password-inp"
                  name="password-inp"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Nhập mật khẩu"
                  className="w-full px-3 py-2 pr-10 text-[13px] bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={auth.isLoading || !username.trim() || !password}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-white text-[13px] font-semibold shadow-sm hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
            >
              {auth.isLoading && (
                <LoaderCircle size={14} className="animate-spin" />
              )}
              {auth.isLoading ? "Đang đăng nhập..." : "Đăng nhập"}
            </button>
          </form>

          <p className="text-[11px] text-gray-400 text-center mt-8">
            Cần hỗ trợ? Liên hệ quản trị viên shop của bạn.
          </p>
        </div>
      </main>
    </section>
  );
};

export default LoginPage;
